import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  CreditCard,
  Inbox,
  Users,
  Cog,
  Palette,
  Compass,
  Shield,
  LayoutDashboard,
  UserCog,
  Database,
  Server,
  Home,
  ChevronDown,
  ChevronRight,
  SearchIcon,
  Bot,
  User,
  Phone,
  Sparkles,
  HelpCircle,
  ArrowLeft,
  FileText,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Brain,
  FolderOpen,
  Plus,
  History,
  Plug,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  folderService,
  type Folder as FolderType,
  type FolderTreeNode,
} from "@/services/folderService";
import { CreateFolderModal } from "@/components/ai-assistant/CreateFolderModal";
import { EditFolderModal } from "@/components/ai-assistant/EditFolderModal";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useImportedSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useProFeature } from "@/components/ui/pro-badge";
import { Badge } from "@/components/ui/badge";
import { useUpgradeToast } from "@/components/ui/upgrade-toast";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

// Main navigation sections (excluding Manage section which is at bottom)
const navigationSections = [
  {
    title: "AI Assistants",
    items: [
      { to: "/ai-assistants", label: "Assistants", icon: Sparkles },
      {
        to: "/library",
        label: "History",
        icon: History,
        comingSoon: false,
      },
    ],
  },
  {
    title: "AI Agents",
    items: [
      {
        to: "/agents",
        label: "My Agents",
        icon: Bot,
        requiresAgents: true,
        isPro: true,
      },
      {
        to: "/voice-calls",
        label: "Calls",
        icon: Phone,
        requiresAgents: true,
        isPro: true,
      },
      {
        to: "/knowledge",
        label: "My Knowledge",
        icon: Brain,
        requiresAgents: true,
        isPro: true,
      },
      {
        to: "/inbox",
        label: "Chats",
        icon: Inbox,
        requiresAgents: true,
        isPro: true,
      },
    ],
    isProSection: true,
  },
];

// Manage section (bottom navigation)
const manageSection = {
  title: "Manage",
  items: [
    { to: "/settings", label: "Settings", icon: Cog },
    { to: "/support", label: "Help", icon: HelpCircle },
  ],
};

// Super Admin navigation structure (only for super admin view)
const superAdminNavigation = [
  {
    title: "Core",
    items: [
      { to: "/super-admin", label: "Overview", icon: Shield },
      {
        to: "/super-admin/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    title: "Content Management",
    items: [
      { to: "/super-admin/templates", label: "Templates", icon: FileText },
      { to: "/super-admin/ai-assistants", label: "AI Assistants", icon: Bot },
    ],
  },
  {
    title: "System Administration",
    items: [
      { to: "/super-admin/users", label: "Users", icon: UserCog },
      {
        to: "/super-admin/organizations",
        label: "Organizations",
        icon: Database,
      },
      { to: "/super-admin/integrations", label: "Integrations", icon: Plug },
      { to: "/super-admin/system", label: "System Settings", icon: Settings },
    ],
  },
];

const AppSidebar = () => {
  const { user, organization } = useAuth();
  const { open, setOpen } = useImportedSidebar();
  const agentsFeature = useProFeature("agents");
  const { showUpgradeToast, ToastContainer } = useUpgradeToast();
  const queryClient = useQueryClient();

  const location = useLocation();
  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);
  const { toast } = useToast();

  // Check if we're in super admin area
  const isInSuperAdmin = location.pathname.startsWith("/super-admin");

  // Conditionally hide agent-related items and chats based on org access and flags
  const getFilteredSections = () => {
    const canUseAgents = agentsFeature.isAllowed;
    const hideChat = organization?.hide_chat_agents ?? false;
    return navigationSections
      .map((section) => {
        if (section.title === "AI Agents") {
          const filteredItems = section.items.filter((item: any) => {
            if (
              !canUseAgents &&
              ["/agents", "/voice-calls", "/knowledge"].includes(item.to)
            )
              return false;
            if (hideChat && item.to === "/inbox") return false;
            return true;
          });
          return {
            ...section,
            items: filteredItems,
            isProSection: !canUseAgents,
          };
        }
        return section;
      })
      .filter((section) => section.items.length > 0); // Remove sections with no items
  };

  const isActive = (path: string) => {
    // For agents section
    if (path === "/agents" && location.pathname.startsWith("/agents")) {
      return true;
    }
    // For ai-assistants section
    if (
      path === "/ai-assistants" &&
      location.pathname.startsWith("/ai-assistants")
    ) {
      return true;
    }
    // For super-admin section
    if (
      path === "/super-admin" &&
      location.pathname.startsWith("/super-admin")
    ) {
      return true;
    }
    // For exact matches
    return location.pathname === path;
  };

  const handleLogoError = () => {
    console.log("🔍 SIDEBAR LOGO DEBUG:", {
      logoUrl: organization?.logo_url,
      errorOccurred: true,
      fallbackToInitials: true,
    });
    setLogoError(true);
  };

  // Helper functions from main branch
  const getOrgInitials = (name: string) => {
    if (!name) return "FA";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const handleAvatarClick = () => {
    navigate("/settings?tab=profile");
  };
  // Folders sidebar: fetch tree and manage modal state
  const orgId = organization?.id || "";
  const { data: folderTree } = useQuery({
    queryKey: ["folderTree", orgId, false],
    queryFn: async () => {
      if (!orgId) return [] as FolderTreeNode[];
      const { data } = await folderService.getFolderTree(orgId, false);
      return data || [];
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [editingFolder, setEditingFolder] = React.useState<FolderType | null>(
    null,
  );
  const [newFolderParentId, setNewFolderParentId] = React.useState<
    string | null
  >(null);
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(
    new Set(),
  );
  const [foldersExpanded, setFoldersExpanded] = React.useState(false);

  const archiveMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: string; archive: boolean }) => {
      const { error } = await folderService.archiveFolder(id, archive);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await folderService.deleteFolder(id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
    },
  });

  const renderFoldersMenu = () => {
    if (isInSuperAdmin) return null;
    const basePathActive = location.pathname.startsWith("/folders");

    // Build children map for hierarchical rendering
    const nodes: FolderTreeNode[] = (folderTree || []) as any;
    const childrenMap = new Map<string | null, FolderTreeNode[]>();
    nodes.forEach((n) => {
      const key = (n as any).parent_folder_id || null;
      const arr = childrenMap.get(key) || [];
      arr.push(n);
      childrenMap.set(key, arr);
    });

    const roots = childrenMap.get(null) || [];

    const hasChildren = (node: any) =>
      (childrenMap.get(node.id) || []).length > 0;

    const toggleExpand = (id: string) => {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    const renderNode = (node: any, depth: number) => {
      const active =
        location.pathname.startsWith("/folders") &&
        location.search.includes(node.id);
      const expanded = expandedFolders.has(node.id);
      return (
        <React.Fragment key={node.id}>
          <div
            className={`flex items-center justify-between px-2 py-1.5 rounded-md text-sm ${
              active
                ? "text-white bg-slate-700"
                : "text-white/90 hover:bg-slate-800/50"
            }`}
            style={{ paddingLeft: 10 + (depth + 1) * 12 }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {hasChildren(node) ? (
                <button
                  className="text-white/70 hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(node.id);
                  }}
                  title={expanded ? "Collapse" : "Expand"}
                >
                  {expanded ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                </button>
              ) : (
                <span style={{ width: 14 }} />
              )}
              <Link
                to={`/folders?folder=${node.id}`}
                className="flex items-center gap-2 min-w-0"
              >
                <FolderOpen
                  size={14}
                  style={{ color: node.folder_color || "#667085" }}
                />
                <span className="truncate">{node.name}</span>
              </Link>
            </div>
            <div className="opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-white/70 hover:text-white"
                  >
                    <span className="inline-block rotate-90">⋮</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    onClick={() => setEditingFolder(node as any)}
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setNewFolderParentId(node.id);
                      setCreateModalOpen(true);
                    }}
                  >
                    New Subfolder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      archiveMutation.mutate({
                        id: node.id,
                        archive: !node.is_archived,
                      })
                    }
                  >
                    {node.is_archived ? "Unarchive" : "Archive"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      if (confirm("Delete this folder?"))
                        deleteMutation.mutate(node.id);
                    }}
                    className="text-red-600"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {expanded &&
            (childrenMap.get(node.id) || []).map((child) =>
              renderNode(child, depth + 1),
            )}
        </React.Fragment>
      );
    };
    return (
      <>
        {/* Folders header row with caret and + button */}
        {open && (
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-white hover:bg-slate-800/50 cursor-pointer transition-colors`}
            onClick={() => setFoldersExpanded(!foldersExpanded)}
          >
            <FolderOpen size={16} style={{ color: "#667085" }} />
            <span className="flex-1">Library</span>
            <div className="flex items-center gap-1">
              {foldersExpanded ? (
                <ChevronDown size={14} className="text-white/70" />
              ) : (
                <ChevronRight size={14} className="text-white/70" />
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 p-0 text-white/80 hover:bg-slate-800/50"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateModalOpen(true);
                }}
                title="New Folder"
              >
                <Plus size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* Nested folders when expanded */}
        {open && foldersExpanded && roots.map((node) => renderNode(node, 0))}

        <CreateFolderModal
          open={createModalOpen}
          onOpenChange={(open) => {
            if (!open) setNewFolderParentId(null);
            setCreateModalOpen(open);
          }}
          parentFolderId={newFolderParentId}
        />
        {editingFolder && (
          <EditFolderModal
            folder={editingFolder}
            open={!!editingFolder}
            onOpenChange={(open) => {
              if (!open) setEditingFolder(null);
            }}
          />
        )}
      </>
    );
  };

  // Render navigation item (collapsed or expanded)
  const renderNavItem = (item: any, section?: any) => {
    const Icon = item.icon;
    const isItemActive = isActive(item.to);
    const isComingSoon = item.comingSoon;
    const isSectionLocked = section?.isProSection && agentsFeature.isPro;
    const isProFeature = false; // No longer needed on individual items

    if (!open) {
      // Collapsed view - show only icon with tooltip
      return (
        <TooltipProvider key={item.to}>
          <Tooltip>
            <TooltipTrigger asChild>
              {isComingSoon ? (
                <div className="flex items-center justify-center w-10 h-10 rounded-md transition-colors cursor-not-allowed text-white/40">
                  <Icon size={18} style={{ color: "#667085" }} />
                </div>
              ) : isSectionLocked ? (
                <div
                  onClick={() =>
                    showUpgradeToast(
                      "Upgrade Required",
                      "AI Agents are available on Pro plans and above.",
                    )
                  }
                  className="flex items-center justify-center w-10 h-10 rounded-md transition-colors text-white/40 cursor-pointer"
                >
                  <Icon size={18} style={{ color: "#667085" }} />
                </div>
              ) : (
                <Link
                  to={item.to}
                  className={`flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                    isItemActive
                      ? "text-white bg-slate-700"
                      : "text-white hover:bg-slate-800/50"
                  }`}
                >
                  <Icon size={18} style={{ color: "#667085" }} />
                </Link>
              )}
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2">
              <p>
                {item.label}
                {isComingSoon
                  ? " (Coming Soon)"
                  : isSectionLocked
                    ? " (Pro Feature)"
                    : ""}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Expanded view - show icon and label
    if (isComingSoon) {
      return (
        <TooltipProvider key={item.to}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={() =>
                  toast({
                    title: "Coming Soon",
                    description: `${item.label} will be available soon.`,
                  })
                }
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-white/70 hover:bg-slate-800/50 cursor-pointer"
              >
                <Icon size={16} style={{ color: "#667085" }} />
                <span>{item.label}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2">
              <p>Coming Soon</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Section is locked (show grayed out)
    if (isSectionLocked) {
      return (
        <div
          key={item.to}
          onClick={() =>
            showUpgradeToast(
              "Upgrade Required",
              "AI Agents are available on Pro plans and above.",
            )
          }
          className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-white/40 hover:bg-slate-800/50 cursor-pointer transition-colors"
        >
          <Icon size={16} style={{ color: "#667085" }} />
          <span>{item.label}</span>
        </div>
      );
    }

    // Regular available feature
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
          isItemActive
            ? "text-white bg-slate-700"
            : "text-white hover:bg-slate-800/50"
        }`}
      >
        <Icon size={16} style={{ color: "#667085" }} />
        <span>{item.label}</span>
      </Link>
    );
  };

  // Render section with items
  const renderSection = (section: any) => {
    if (!open) {
      // Collapsed view - render items without section title
      return (
        <div key={section.title} className="space-y-1">
          {section.items.map((item: any) => renderNavItem(item, section))}
        </div>
      );
    }

    // Expanded view - render with section title
    const isSectionLocked = section.isProSection && !agentsFeature.isAllowed;

    return (
      <div key={section.title} className="space-y-2">
        <div className="flex items-center justify-between px-2">
          <h3 className="text-[11px] font-normal uppercase tracking-wider text-white/60">
            {section.title}
          </h3>
          {isSectionLocked && (
            <Badge
              className="ml-2 text-xs px-2 py-0 h-5 bg-blue-600 text-white cursor-pointer hover:bg-blue-700"
              onClick={() => navigate("/settings?tab=subscription")}
            >
              Unlock
            </Badge>
          )}
        </div>
        <div className="space-y-0.5">
          {section.items.map((item: any) => renderNavItem(item, section))}
        </div>
      </div>
    );
  };

  return (
    <>
      <ToastContainer />
      <Sidebar
        className={open ? "w-[220px]" : "w-[60px]"}
        style={{ backgroundColor: "hsl(var(--primary))" }}
        collapsible="icon"
      >
        <SidebarContent
          className="p-0"
          style={{ backgroundColor: "hsl(var(--primary))" }}
        >
          {/* Header with Logo and Collapse Toggle */}
          <div className={`${open ? "p-4" : "p-2"} border-b border-white/20`}>
            <div className="flex items-center justify-between">
              {open && (
                <div className="flex justify-center flex-1">
                  <img
                    src="https://gvaimfxjrywshlnkxrbi.supabase.co/storage/v1/object/public/image_library/public_images/logo%20horizontal%20white%20agents.svg"
                    alt="FirmAgent Logo"
                    className="h-7 w-auto max-w-[160px] object-contain"
                  />
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(!open)}
                className="text-white hover:bg-slate-800/50 p-1 h-8 w-8"
              >
                {open ? (
                  <PanelLeftClose size={16} />
                ) : (
                  <PanelLeftOpen size={16} />
                )}
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className={`${open ? "p-3" : "p-2"} space-y-2.5`}>
              {isInSuperAdmin ? (
                <>
                  {/* Back to App Button */}
                  <div className="pb-3 border-b border-white/20">
                    {!open ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate("/agents")}
                              className="flex items-center justify-center w-10 h-10 rounded-md text-white hover:bg-slate-800/50 transition-colors"
                            >
                              <ArrowLeft
                                size={16}
                                style={{ color: "#667085" }}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="ml-2">
                            <p>Back to App</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <button
                        onClick={() => navigate("/agents")}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-white hover:bg-slate-800/50 transition-colors w-full"
                      >
                        <ArrowLeft size={16} style={{ color: "#667085" }} />
                        <span>Back to App</span>
                      </button>
                    )}
                  </div>

                  {/* Super Admin Navigation */}
                  {superAdminNavigation.map((section) =>
                    renderSection(section),
                  )}
                </>
              ) : (
                <>
                  {/* Main Navigation Sections */}
                  {getFilteredSections().map((section, index) => (
                    <div
                      key={section.title}
                      className={index > 0 ? "mt-4" : ""}
                    >
                      {renderSection(section)}
                      {section.title === "AI Assistants" && renderFoldersMenu()}
                    </div>
                  ))}
                </>
              )}
            </div>
          </ScrollArea>
        </SidebarContent>

        {/* Footer with Manage Section and Organization Info */}
        <SidebarFooter
          className={`${open ? "p-3" : "p-2"}`}
          style={{ backgroundColor: "hsl(var(--primary))" }}
        >
          {!isInSuperAdmin && (
            <div className="space-y-2">
              {/* Manage Section */}
              {open ? (
                <div className="space-y-0.5">
                  {manageSection.items.map((item) => renderNavItem(item))}
                </div>
              ) : (
                <div className="space-y-1">
                  {manageSection.items.map((item) => renderNavItem(item))}
                </div>
              )}
            </div>
          )}

          {/* Organization Info */}
          {open ? (
            <div className="border-t border-white/20 pt-3 mt-3">
              <div className="flex items-center gap-2 px-2 py-1">
                {organization?.logo_url && !logoError ? (
                  <img
                    src={organization.logo_url}
                    alt={`${organization.name} logo`}
                    className="h-5 w-5 rounded object-cover"
                    onError={handleLogoError}
                  />
                ) : (
                  <div className="h-5 w-5 bg-gray-400 rounded flex items-center justify-center text-white font-semibold text-xs">
                    {organization?.name?.charAt(0)?.toUpperCase() || "O"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-xs text-white truncate">
                    {organization?.name || "Organization"}
                  </h2>
                </div>
              </div>
            </div>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="border-t border-white/20 pt-3 mt-3">
                    <div className="flex justify-center">
                      {organization?.logo_url && !logoError ? (
                        <img
                          src={organization.logo_url}
                          alt={`${organization.name} logo`}
                          className="h-6 w-6 rounded object-cover"
                          onError={handleLogoError}
                        />
                      ) : (
                        <div className="h-6 w-6 bg-gray-400 rounded flex items-center justify-center text-white font-semibold text-xs">
                          {organization?.name?.charAt(0)?.toUpperCase() || "O"}
                        </div>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="ml-2">
                  <p>{organization?.name || "Organization"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </SidebarFooter>
      </Sidebar>
    </>
  );
};

export default AppSidebar;
